import fields from '@/sui/data/sheets/edit/fields'
import patient from '@/sui/data/sheets/edit/patient'
import catheter from '@/sui/data/sheets/edit/catheter'
import tools from '@/sui/data/sheets/edit/tools'
import micro from '@/sui/data/sheets/edit/micro'
import multi from '@/sui/data/sheets/edit/multi'
import intub from '@/sui/data/sheets/edit/intub'
import assert from '@/sui/tools/assert'
import go from '@/gojs/go'

const titles = {
  insertionDate: 'de pose du cathéter'
}

function listFields (list, prefix) {
  const result = {}
  for (let i = 0; i !== list.length; ++i) {
    const k = list[i]
    const f = fields[k]
    assert.notNull(f, `${k} should exist in fields`)
    result[k] = prefix == null ? f : `${prefix}${f}`
  }
  return result
}

const bactFields = (function () {
  const list = [
    'entrance',
    'urinary',
    'urinaryRemoved',
    'removed',
    'enfCatheter',
    'enfCatheterType',
    'previousManip',
    'divDiag',
    'enfPreviousManip'
  ]
  return {
    std: listFields(list),
    sep: listFields(list, 'sep')
  }
})()

const select = (function () {
  const bact = tools.equals(fields.bacteremia, 1)

  const ktEntrances = /^(C|D1)/
  const isKtEntrance = {
    v1: tools.inf(fields.entrance, 10),
    v2: tools.match(bactFields.std.entrance, ktEntrances),
    sep: tools.match(bactFields.sep.entrance, ktEntrances)
  }

  const ktWithLipids = {
    v1: /^C[1234]$/,
    v2: {
      std: /^C([123489]|12)$/,
      rea: /C(9|12)$/,
      neo: /^C([289]|12)$/
    }
  }
  const isKtWithLipids = {
    v1: tools.match(bactFields.std.entrance, ktWithLipids.v1),
    v2: {
      std: tools.match(bactFields.std.entrance, ktWithLipids.v2.std),
      rea: tools.match(bactFields.std.entrance, ktWithLipids.v2.rea),
      neo: tools.match(bactFields.std.entrance, ktWithLipids.v2.neo)
    },

    sep: {
      v1: tools.match(bactFields.sep.entrance, ktWithLipids.v1),
      v2: tools.match(bactFields.sep.entrance, ktWithLipids.v2.std)
    }
  }

  const isKtRemoved = {
    v1: tools.allTrue(isKtEntrance.v1, tools.equals(fields.removed, 1)),
    v2: tools.allTrue(isKtEntrance.v2, tools.equals(bactFields.std.removed, 1)),
    sep: tools.allTrue(isKtEntrance.sep, tools.equals(bactFields.sep.removed, 1))
  }

  const ktWithManip = /^(C2|C4|C8)$/
  const isKtWithManip = tools.match(bactFields.std.entrance, ktWithManip)
  const hasPreviousManip = tools.allTrue(
    isKtWithManip,
    tools.equals(bactFields.std.previousManip, 1)
  )

  const hasClinicalDiag = tools.allTrue(
    isKtEntrance.v2,
    tools.equals(bactFields.std.divDiag, 5)
  )

  const uriEntrance = 'A4'
  const isUriEntrance = {
    std: tools.equals(bactFields.std.entrance, uriEntrance),
    sep: tools.equals(bactFields.sep.entrance, uriEntrance)
  }

  const withUrinaryProbe = {
    v1: {
      std: tools.allTrue(
        isUriEntrance.std, tools.equals2(bactFields.std.urinary, 1, 3)
      ),
      sep: tools.allTrue(
        isUriEntrance.sep, tools.equals2(bactFields.sep.urinary, 1, 3)
      )
    },

    v2: {
      std: tools.allTrue(
        isUriEntrance.std,
        tools.inList(bactFields.std.urinary, [2, 3, 4, 5, 6])
      )
    }
  }

  const isUriRemoved = {
    v1: {
      std: tools.allTrue(
        withUrinaryProbe.v1.std, tools.equals(bactFields.std.urinaryRemoved, 1)
      ),
      sep: tools.allTrue(
        withUrinaryProbe.v1.sep, tools.equals(bactFields.sep.urinaryRemoved, 1)
      )
    },

    v2: {
      std: tools.allTrue(
        withUrinaryProbe.v2.std, tools.equals(bactFields.std.urinaryRemoved, 1)
      )
    }
  }

  const nfEntrance = { v1: 24, v2: 'A13' }
  const entranceNotFound = {
    v1: tools.equals(fields.entrance, nfEntrance.v1),
    v2: tools.equals(bactFields.std.entrance, nfEntrance.v2),
    sep: tools.equals(bactFields.sep.entrance, nfEntrance.v2)
  }

  const entranceNotFoundWithKt = {
    v1: tools.allTrue(entranceNotFound.v1, tools.equals(fields.enfCatheter, 1)),
    v2: tools.allTrue(entranceNotFound.v2, tools.equals(bactFields.std.enfCatheter, 1)),
    sep: tools.allTrue(
      entranceNotFound.sep, tools.equals(bactFields.sep.enfCatheter, 1)
    )
  }

  const enfKtWithManip = tools.allTrue(
    entranceNotFoundWithKt.v2,
    tools.match(bactFields.std.enfCatheterType, ktWithManip)
  )
  const enfKtWithPreviousManip = tools.allTrue(
    enfKtWithManip,
    tools.equals(bactFields.std.enfPreviousManip, 1)
  )

  const pleu = {
    v2: tools.equals(bactFields.std.entrance, 'A3'),
    sep: tools.equals(bactFields.sep.entrance, 'A3')
  }

  const neoisKtEntrance = tools.allTrue(isKtEntrance.v2, patient.select.isNeoOrNeoMix)

  return {
    bact,
    isKtEntrance,
    isKtWithLipids,
    isKtRemoved,
    isKtWithManip,
    hasPreviousManip,
    enfKtWithManip,
    enfKtWithPreviousManip,
    hasClinicalDiag,
    isUriEntrance,
    withUrinaryProbe,
    isUriRemoved,
    entranceNotFound,
    entranceNotFoundWithKt,
    pleu,
    neoisKtEntrance
  }
})()

const bacteremiaPresent = tools.cfg.list(
  fields.bacteremia, 'Bactériémie', go.fields.yesNoUnknown, {
    tt: `Taper le chiffre ou quelques lettres du choix.
Pour clore la liste ou supprimer une entrée, sélectionner «2. Non» ou
«9. Non Connu».`,
    def: 1,
    pop: tools.not(select.bact)
  }
)

const episod = tools.cfg.date(fields.episod, `de l'épisode`, {
  tt: `jj/mm/aaaa, postérieure à la date d'entrée.`
})

const diagDate = (function () {
  const base = tools.cfg.date(fields.diagDate, 'du diagnostic')
  return {
    base,
    v1: base,
    v2: tools.opts(base, { op: true })
  }
})()

const bactOrigin = (function () {
  function build (values) {
    return tools.cfg.list(fields.bactOrigin, 'Origine', values)
  }

  return {
    v1: build(go.fields2019.bactOrigin),
    v2: build(go.fields.bactOrigin)
  }
})()

const entrance = (function () {
  function build (values, keys) {
    return tools.cfg.list(
      fields.entrance, `Porte d'entrée`, values, {
        ph: `Taper le nombre ou quelques lettres de la localisation...`,
        tt: `Taper le nombre ou quelques lettres de la localisation de la porte
d'entrée : ${tools.key(keys.endo)} ou ${tools.key('end')} pour une
endocardite, ${tools.key(keys.peri)} ou ${tools.key('péri')} pour une
dialyse péritonéale, etc.`
      }
    )
  }

  const entranceKeys = {
    v1: { endo: '21', peri: '12' },
    v2: { endo: 'A9', peri: 'D4' }
  }

  return {
    v1: {
      all: build(go.fields2019.entrance, entranceKeys.v1),
      peri: build(go.fields2019.periEntrance, entranceKeys.v1),
      dia: build(go.fields2019.diaEntrance, entranceKeys.v1)
    },

    v2: {
      all: build(go.fields2020.entrance, entranceKeys.v2),
      peri: build(go.fields2024.periEntrance, entranceKeys.v2),
      dia: build(go.fields.diaEntrance, entranceKeys.v2)
    },

    v3: {
      all: build(go.fields.entrance, entranceKeys.v2),
      peri: build(go.fields.periEntrance, entranceKeys.v2),
      neo: build(go.fields2021.neoPeriEntrance, entranceKeys.v2)
    },

    v4: {
      neo: build(go.fields2025.neoBactEntrance, entranceKeys.v2)
    },

    v5: {
      neo: build(go.fields.neoBactEntrance, entranceKeys.v2)
    }
  }
})()

const insertionDate = {
  base: catheter.insertionDate,
  v1: tools.select(catheter.insertionDate, select.isKtEntrance.v1),
  v2: tools.select(catheter.insertionDate, select.isKtEntrance.v2),
  sep: tools.select(catheter.insertionDate, select.isKtEntrance.sep)
}

const previousManip = tools.cfg.list(
  fields.previousManip, `Manipulation dans les 7j précédant la date de l'épisode`, go.fields.yesNoUnknown,
  { sel: select.isKtWithManip }
)

const previousManipType = (function () {
  function build (f) {
    return tools.cfg.list(
      fields.previousManipType, `Type de manipulation`, f, { sel: select.hasPreviousManip }
    )
  }

  return {
    v1: build(go.fields2023.previousManipType),
    v2: build(go.fields.previousManipType)
  }
})()

const previousManipDate = tools.cfg.date(
  fields.previousManipDate, 'de la manipulation', { sel: select.hasPreviousManip, op: true }
)

const insertionSite = (function () {
  function build (values, sel) {
    const opts = sel == null ? {} : { sel: sel }
    return tools.cfg.list(
      fields.insertionSite, 'Site de pose', values, opts
    )
  }

  return {
    v1: {
      all: build(go.fields2019.insertionSite, select.isKtEntrance.v1),
      woOmbilical: build(
        go.fields2019.insertionSiteWithoutOmbilical,
        select.isKtEntrance.v1
      )
    },
    v2: {
      all: build(go.fields2021.insertionSite, select.isKtEntrance.v2),
      woOmbilical: build(
        go.fields2021.insertionSiteWithoutOmbilical, select.isKtEntrance.v2
      )
    },
    v3: {
      all: build(go.fields.insertionSite, select.isKtEntrance.v2),
      woOmbilical: build(
        go.fields.insertionSiteWithoutOmbilical, select.isKtEntrance.v2
      )
    },

    sep: {
      v1: build(go.fields2021.insertionSite, select.isKtEntrance.sep),
      v2: build(go.fields.insertionSite, select.isKtEntrance.sep)
    },

    full: build(go.fields.insertionSiteFull, null)
  }
})()

const removed = {
  v1: tools.select(catheter.removed, select.isKtEntrance.v1),
  v2: tools.select(catheter.removed, select.isKtEntrance.v2),
  sep: tools.select(catheter.removed, select.isKtEntrance.sep)
}

const removalDate = {
  base: catheter.removalDate,

  v1: tools.select(catheter.removalDate, select.isKtRemoved.v1),
  v2: tools.select(catheter.removalDate, select.isKtRemoved.v2),
  sep: tools.select(catheter.removalDate, select.isKtRemoved.sep)
}

const divDiag = (function () {
  function build (f, gf, o) {
    return tools.cfg.list(f, 'Critère diagnostique', gf, o)
  }

  return {
    v1: {
      adped: build(
        fields.divDiag, go.fields2020.divDiag, {
          sel: tools.allTrue(
            select.isKtEntrance.v2,
            (d, s) => patient.select.isAdPedOrPedMix(d, s)
          )
        }
      ),
      neo: build(fields.neoDivDiag, go.fields2020.neoDivDiag)
    },

    v2: build(
      fields.divDiag, go.fields.divDiag, {
        sel: select.isKtEntrance.v2,
        ms: true
      }
    )
  }
})()

const clinicalSigns = tools.cfg.list(
  fields.clinicalSigns, 'Signes cliniques', go.fields.yesNoUnknown,
  { sel: select.hasClinicalDiag }
)

const bioSigns = tools.cfg.list(
  fields.bioSigns, 'Signes biologiques', go.fields.yesNoUnknown,
  { sel: select.hasClinicalDiag }
)

const adaptedAntibio = tools.cfg.list(
  fields.adaptedAntibio,
  `Mise en place d'une antibiothérapie adaptée d'au moins 5 jours`,
  go.fields.yesNoUnknown,
  { sel: select.hasClinicalDiag }
)

const twoHemocs = tools.cfg.list(
  fields.twoHemocs,
  `Deux hémocultures faites à des moments différents`,
  go.fields.yesNoUnknown,
  { sel: select.hasClinicalDiag }
)

const lipids = (function () {
  function build (sel) {
    return tools.cfg.list(
      fields.lipids, 'Lipides / Alimentation parentérale',
      go.fields.yesNoUnknown, { sel: sel }
    )
  }

  return {
    v2: build(select.isKtEntrance.v2),
    v3: build(select.isKtWithLipids.v1),
    v4: {
      std: build(select.isKtWithLipids.v2.std),
      rea: build(select.isKtWithLipids.v2.rea),
      neo: build(select.isKtWithLipids.v2.neo)
    },
    sep: {
      v1: build(select.isKtEntrance.sep),
      v2: build(select.isKtWithLipids.sep.v1),
      v3: build(select.isKtWithLipids.sep.v2)
    }
  }
})()

const ktWithTunnel = /^D1$/

const tunneled = tools.cfg.list(
  fields.tunneled, 'Cathéter tunnélisé', go.fields.yesNoUnknown,
  { sel: tools.match(bactFields.std.entrance, ktWithTunnel) }
)

const enfTunneled = tools.cfg.list(
  fields.enfTunneled, 'Cathéter tunnélisé', go.fields.yesNoUnknown,
  {
    sel: tools.allTrue(
      select.entranceNotFound.v2,
      tools.match(bactFields.std.enfCatheterType, ktWithTunnel)
    )
  }
)

const urinary = (function () {
  function build (i, f, op) {
    const options = {
      sel: i
    }

    if (op) {
      options.op = true
      options.ov = 'Ne participe pas au volet optionnel sondage urinaire'
    }

    return tools.cfg.list(fields.urinary, 'Sondage urinaire', f, options)
  }

  return {
    v1: {
      std: build(select.isUriEntrance.std, go.fields2020.urinary, true),
      exp: build(select.isUriEntrance.std, go.fields2020.urinary, true),
      sep: build(select.isUriEntrance.sep, go.fields2020.urinaryNoSAD, true)
    },

    v2: {
      std: build(select.isUriEntrance.std, go.fields.urinary, false),
      sep: build(select.isUriEntrance.sep, go.fields.yesNoUnknown, false)
    }
  }
})()

const urinaryStart = (function () {
  const base = tools.cfg.date(
    fields.urinaryStart, 'de pose', { op: true }
  )

  return {
    base: base,

    v1: {
      std: tools.select(base, select.withUrinaryProbe.v1.std),
      sep: tools.select(base, select.withUrinaryProbe.v1.sep)
    },

    v2: {
      std: tools.select(base, select.withUrinaryProbe.v2.std)
    }
  }
})()

const urinaryRemoved = (function () {
  function build (i) {
    return tools.cfg.list(
      fields.urinaryRemoved, 'Retrait', go.fields.yesNoUnknown,
      { sel: i }
    )
  }

  return {
    v1: {
      std: build(select.withUrinaryProbe.v1.std),
      sep: build(select.withUrinaryProbe.v1.sep)
    },

    v2: {
      std: build(select.withUrinaryProbe.v2.std)
    }
  }
})()

const urinaryEnd = (function () {
  const base = tools.cfg.date(
    fields.urinaryEnd, 'de retrait', { op: true }
  )

  return {
    base: base,

    v1: {
      std: tools.select(base, select.isUriRemoved.v1.std),
      sep: tools.select(base, select.isUriRemoved.v1.sep)
    },

    v2: {
      std: tools.select(base, select.isUriRemoved.v2.std)
    }
  }
})()

const urinaryDiag = (function () {
  function build (i) {
    return tools.cfg.list(
      fields.urinaryDiag, 'Critère diagnostique', go.fields.urinaryDiag, {
        sel: i
      }
    )
  }

  return {
    v1: build(select.withUrinaryProbe.v1.std),
    v2: build(select.withUrinaryProbe.v2.std)
  }
})()

const enfCatheter = (function () {
  function build (sel) {
    return tools.cfg.list(
      fields.enfCatheter, 'Cathéter présent', go.fields.yesNoUnknown,
      { sel: sel }
    )
  }

  return {
    v1: build(select.entranceNotFound.v1),
    v2: build(select.entranceNotFound.v2),
    sep: build(select.entranceNotFound.sep)
  }
})()

const enfCatheterType = (function () {
  function build (values, sel) {
    return tools.cfg.list(
      fields.enfCatheterType, 'Type de cathéter', values, { sel: sel }
    )
  }

  return {
    v1: {
      all: build(go.fields2019.enfCatheterType, select.entranceNotFoundWithKt.v1),
      neo: build(go.fields2019.enfNeoCatheterType, select.entranceNotFoundWithKt.v1),
      dia: build(go.fields2019.enfDiaCatheterType, select.entranceNotFoundWithKt.v1),
      peri: build(go.fields2019.enfReaInfCatheterType, select.entranceNotFoundWithKt.v1)
    },
    v2: {
      all: build(go.fields2020.enfCatheterType, select.entranceNotFoundWithKt.v2),
      peri: build(go.fields.enfPeriType, select.entranceNotFoundWithKt.v2),
      dia: build(go.fields.enfDiaType, select.entranceNotFoundWithKt.v2),
      sep: build(go.fields2020.enfCatheterType, select.entranceNotFoundWithKt.sep)
    },
    v3: {
      all: build(go.fields.enfCatheterType, select.entranceNotFoundWithKt.v2),
      sep: build(go.fields.enfCatheterType, select.entranceNotFoundWithKt.sep),
      neo: build(go.fields2021.enfNeoCatheterType, select.entranceNotFoundWithKt.v2)
    },
    v4: {
      neo: build(go.fields.enfNeoCatheterType, select.entranceNotFoundWithKt.v2)
    }
  }
})()

const enfInsertionDate = (function () {
  const base = tools.cfg.date(
    fields.enfInsertionDate, titles.insertionDate, { op: true }
  )

  function build (ign) {
    return tools.select(base, ign)
  }

  return {
    base: base,
    v1: build(select.entranceNotFoundWithKt.v1),
    v2: build(select.entranceNotFoundWithKt.v2),
    sep: build(select.entranceNotFoundWithKt.sep)
  }
})()

const enfPreviousManip = tools.cfg.list(
  fields.enfPreviousManip,
  `Manipulation dans les 7j précédant la date de l'épisode`,
  go.fields.yesNoUnknown,
  { sel: select.enfKtWithManip }
)

const enfPreviousManipType = (function () {
  function build (f) {
    return tools.cfg.list(
      fields.enfPreviousManipType, `Type de manipulation`,
      f, { sel: select.enfKtWithPreviousManip }
    )
  }
  return {
    v1: build(go.fields2023.previousManipType),
    v2: build(go.fields.previousManipType)
  }
})()

const enfPreviousManipDate = tools.cfg.date(
  fields.enfPreviousManipDate, 'de la manipulation',
  { sel: select.enfKtWithPreviousManip, op: true }
)

const diaOrigin = tools.cfg.list(
  fields.bactOrigin, 'Origine suspectée', go.fields.diaOrigin
)

const neoOrigin = (function () {
  function build (f) {
    return tools.cfg.list(fields.bactOrigin, 'Origine', f)
  }

  return {
    v1: build(go.fields2019.neoInfOrigin),
    v2: build(go.fields.bactOriginNoEhpad)
  }
})()

const episodImmuno = tools.cfg.list(
  fields.episodImmuno, `Immunodépression au moment de l'épisode`,
  go.fields.immuno, { sel: tools.match(fields.unitType, /(HEM|CAN)/) }
)

const bacteremia = {
  v1: {
    flat: [
      episod,
      bactOrigin.v1,
      micro.v1.noVirus,
      entrance.v1.all,
      insertionDate.v1,
      insertionSite.v1.all,
      removed.v1,
      removalDate.v1,
      enfCatheter.v1,
      enfCatheterType.v1.all,
      enfInsertionDate.v1
    ],

    array: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(bactOrigin.v1, select.bact),
      tools.andSelect(micro.v1.noVirus, select.bact),
      tools.andSelect(entrance.v1.all, select.bact),
      tools.andSelect(insertionDate.v1, select.bact),
      tools.andSelect(insertionSite.v1.all, select.bact),
      tools.andSelect(removed.v1, select.bact),
      tools.andSelect(removalDate.v1, select.bact),
      tools.andSelect(enfCatheter.v1, select.bact),
      tools.andSelect(enfCatheterType.v1.all, select.bact),
      tools.andSelect(enfInsertionDate.v1, select.bact)
    ],

    extra: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(bactOrigin.v1, select.bact),
      tools.andSelect(micro.v1.noVirus, select.bact),
      tools.andSelect(entrance.v1.peri, select.bact),
      tools.andSelect(insertionDate.v1, select.bact),
      tools.andSelect(insertionSite.v1.woOmbilical, select.bact),
      tools.andSelect(removed.v1, select.bact),
      tools.andSelect(removalDate.v1, select.bact),
      tools.andSelect(enfCatheter.v1, select.bact),
      tools.andSelect(enfCatheterType.v1.peri, select.bact),
      tools.andSelect(enfInsertionDate.v1, select.bact)
    ],

    dia: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diaOrigin, select.bact),
      tools.andSelect(entrance.v1.dia, select.bact),
      tools.andSelect(micro.v1.noVirus, select.bact),
      tools.andSelect(insertionDate.v1, select.bact),
      tools.andSelect(insertionSite.v1.all, select.bact),
      tools.andSelect(removed.v1, select.bact),
      tools.andSelect(removalDate.v1, select.bact),
      tools.andSelect(enfCatheter.v1, select.bact),
      tools.andSelect(enfCatheterType.v1.dia, select.bact),
      tools.andSelect(enfInsertionDate.v1, select.bact)
    ],

    neo: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(neoOrigin.v1, select.bact),
      tools.andSelect(micro.v1.noVirus, select.bact),
      tools.andSelect(entrance.v1.peri, select.bact),
      tools.andSelect(insertionDate.v1, select.bact),
      tools.andSelect(insertionSite.v1.all, select.bact),
      tools.andSelect(removed.v1, select.bact),
      tools.andSelect(removalDate.v1, select.bact),
      tools.andSelect(enfCatheter.v1, select.bact),
      tools.andSelect(enfCatheterType.v1.neo, select.bact),
      tools.andSelect(enfInsertionDate.v1, select.bact)
    ]
  },
  v2: {
    flat: [
      episod,
      diagDate.v1,
      bactOrigin.v2,
      multi.covidStatus.std,
      entrance.v2.all,
      insertionDate.v2,
      insertionSite.v2.all,
      removed.v2,
      removalDate.v2,
      divDiag.v1.adped,
      tools.select(divDiag.v1.neo, select.neoisKtEntrance),
      lipids.v2,
      urinary.v1.std,
      urinaryStart.v1.std,
      urinaryRemoved.v1.std,
      urinaryEnd.v1.std,
      urinaryDiag.v1,
      enfCatheter.v2,
      enfCatheterType.v2.all,
      enfInsertionDate.v2,
      micro.v2.noVirus
    ],

    neo: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v1, select.bact),
      tools.andSelect(neoOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v2.peri, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(insertionSite.v2.woOmbilical, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v1.neo, select.isKtEntrance.v2), select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v2.all, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(micro.v2.noVirus, select.bact)
    ],

    extra: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v1, select.bact),
      tools.andSelect(bactOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v2.peri, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(insertionSite.v2.woOmbilical, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(divDiag.v1.adped, select.bact),
      tools.andSelect(tools.select(divDiag.v1.neo, select.neoisKtEntrance), select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v2.all, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(micro.v2.noVirus, select.bact)
    ],

    dia: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v1, select.bact),
      tools.andSelect(bactOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v2.dia, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(insertionSite.v2.all, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(divDiag.v1.adped, select.bact),
      tools.andSelect(tools.select(divDiag.v1.neo, select.neoisKtEntrance), select.bact),
      tools.andSelect(lipids.v2, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v2.dia, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(micro.v2.noVirus, select.bact)
    ],

    exp: [
      tools.andSelect(episod, multi.select.bact),
      tools.andSelect(diagDate.v1, multi.select.bact),
      tools.andSelect(bactOrigin.v2, multi.select.bact),
      tools.andSelect(multi.covidStatus.std, multi.select.bact),
      tools.andSelect(entrance.v2.all, multi.select.bact),
      tools.andSelect(insertionDate.v2, multi.select.bact),
      tools.andSelect(insertionSite.v2.all, multi.select.bact),
      tools.andSelect(removed.v2, multi.select.bact),
      tools.andSelect(removalDate.v2, multi.select.bact),
      tools.andSelect(divDiag.v1.adped, multi.select.bact),
      tools.andSelect(tools.select(divDiag.v1.neo, select.neoisKtEntrance), multi.select.bact),
      tools.andSelect(lipids.v2, multi.select.bact),
      tools.andSelect(tools.select(intub.intubated.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.intubStart.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(urinary.v1.exp, multi.select.bact),
      tools.andSelect(urinaryStart.v1.std, multi.select.bact),
      tools.andSelect(urinaryRemoved.v1.std, multi.select.bact),
      tools.andSelect(urinaryEnd.v1.std, multi.select.bact),
      tools.andSelect(urinaryDiag.v1, multi.select.bact),
      tools.andSelect(enfCatheter.v2, multi.select.bact),
      tools.andSelect(enfCatheterType.v2.all, multi.select.bact),
      tools.andSelect(enfInsertionDate.v2, multi.select.bact),
      tools.andSelect(micro.v2.noVirus, multi.select.bact)
    ]
  },

  v3: {
    flat: [
      episod,
      diagDate.v1,
      bactOrigin.v2,
      multi.covidStatus.std,
      entrance.v3.all,
      insertionDate.v2,
      previousManip,
      previousManipType.v1,
      previousManipDate,
      insertionSite.v2.all,
      removed.v2,
      removalDate.v2,
      tools.select(divDiag.v2, select.isKtEntrance.v2),
      clinicalSigns,
      bioSigns,
      adaptedAntibio,
      twoHemocs,
      lipids.v2,
      tools.select(intub.intubated.bact, select.pleu.v2),
      tools.andSelect(intub.intubStart.bact, select.pleu.v2),
      tools.andSelect(intub.reintubated.bact, select.pleu.v2),
      tools.andSelect(intub.intubEnd.bact, select.pleu.v2),
      urinary.v2.std,
      urinaryStart.v2.std,
      urinaryRemoved.v2.std,
      urinaryEnd.v2.std,
      urinaryDiag.v2,
      enfCatheter.v2,
      enfCatheterType.v3.all,
      enfInsertionDate.v2,
      enfPreviousManip,
      enfPreviousManipType.v1,
      enfPreviousManipDate,
      micro.v3.noVirus
    ],

    exp: [
      tools.andSelect(episod, multi.select.bact),
      tools.andSelect(diagDate.v1, multi.select.bact),
      tools.andSelect(bactOrigin.v2, multi.select.bact),
      tools.andSelect(multi.covidStatus.std, multi.select.bact),
      tools.andSelect(entrance.v3.all, multi.select.bact),
      tools.andSelect(insertionDate.v2, multi.select.bact),
      tools.andSelect(previousManip, multi.select.bact),
      tools.andSelect(previousManipType.v1, multi.select.bact),
      tools.andSelect(previousManipDate, multi.select.bact),
      tools.andSelect(insertionSite.v2.all, multi.select.bact),
      tools.andSelect(removed.v2, multi.select.bact),
      tools.andSelect(removalDate.v2, multi.select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), multi.select.bact),
      tools.andSelect(clinicalSigns, multi.select.bact),
      tools.andSelect(bioSigns, multi.select.bact),
      tools.andSelect(adaptedAntibio, multi.select.bact),
      tools.andSelect(twoHemocs, multi.select.bact),
      tools.andSelect(lipids.v2, multi.select.bact),
      tools.andSelect(tools.select(intub.intubated.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.intubStart.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.reintubated.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.intubEnd.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(urinary.v2.std, multi.select.bact),
      tools.andSelect(urinaryStart.v2.std, multi.select.bact),
      tools.andSelect(urinaryRemoved.v2.std, multi.select.bact),
      tools.andSelect(urinaryEnd.v2.std, multi.select.bact),
      tools.andSelect(urinaryDiag.v2, multi.select.bact),
      tools.andSelect(enfCatheter.v2, multi.select.bact),
      tools.andSelect(enfCatheterType.v3.all, multi.select.bact),
      tools.andSelect(enfInsertionDate.v2, multi.select.bact),
      tools.andSelect(enfPreviousManip, multi.select.bact),
      tools.andSelect(enfPreviousManipType.v1, multi.select.bact),
      tools.andSelect(enfPreviousManipDate, multi.select.bact),
      tools.andSelect(micro.v3.noVirus, multi.select.bact)
    ],

    extra: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v1, select.bact),
      tools.andSelect(bactOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v2.peri, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v1, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v2.woOmbilical, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(lipids.v2, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v2.peri, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v1, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v3.noVirus, select.bact)
    ],

    neo: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v1, select.bact),
      tools.andSelect(neoOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v3.neo, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v1, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v2.woOmbilical, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v3.neo, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v1, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v3.noVirus, select.bact)
    ],

    dia: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v1, select.bact),
      tools.andSelect(bactOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v2.dia, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v1, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v2.all, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(lipids.v2, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v2.dia, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v1, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v3.noVirus, select.bact)
    ]
  },

  v4: {
    flat: [
      episod,
      diagDate.v1,
      bactOrigin.v2,
      multi.covidStatus.std,
      entrance.v3.all,
      insertionDate.v2,
      previousManip,
      previousManipType.v1,
      previousManipDate,
      insertionSite.v3.all,
      removed.v2,
      removalDate.v2,
      tools.select(divDiag.v2, select.isKtEntrance.v2),
      clinicalSigns,
      bioSigns,
      adaptedAntibio,
      twoHemocs,
      lipids.v3,
      tools.select(intub.intubated.bact, select.pleu.v2),
      tools.andSelect(intub.intubStart.bact, select.pleu.v2),
      tools.andSelect(intub.reintubated.bact, select.pleu.v2),
      tools.andSelect(intub.intubEnd.bact, select.pleu.v2),
      urinary.v2.std,
      urinaryStart.v2.std,
      urinaryRemoved.v2.std,
      urinaryEnd.v2.std,
      urinaryDiag.v2,
      enfCatheter.v2,
      enfCatheterType.v3.all,
      enfInsertionDate.v2,
      enfPreviousManip,
      enfPreviousManipType.v1,
      enfPreviousManipDate,
      micro.v3.noVirus
    ],

    exp: [
      tools.andSelect(episod, multi.select.bact),
      tools.andSelect(diagDate.v1, multi.select.bact),
      tools.andSelect(bactOrigin.v2, multi.select.bact),
      tools.andSelect(multi.covidStatus.std, multi.select.bact),
      tools.andSelect(entrance.v3.all, multi.select.bact),
      tools.andSelect(insertionDate.v2, multi.select.bact),
      tools.andSelect(previousManip, multi.select.bact),
      tools.andSelect(previousManipType.v1, multi.select.bact),
      tools.andSelect(previousManipDate, multi.select.bact),
      tools.andSelect(insertionSite.v3.all, multi.select.bact),
      tools.andSelect(removed.v2, multi.select.bact),
      tools.andSelect(removalDate.v2, multi.select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), multi.select.bact),
      tools.andSelect(clinicalSigns, multi.select.bact),
      tools.andSelect(bioSigns, multi.select.bact),
      tools.andSelect(adaptedAntibio, multi.select.bact),
      tools.andSelect(twoHemocs, multi.select.bact),
      tools.andSelect(lipids.v3, multi.select.bact),
      tools.andSelect(tools.select(intub.intubated.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.intubStart.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.reintubated.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.intubEnd.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(urinary.v2.std, multi.select.bact),
      tools.andSelect(urinaryStart.v2.std, multi.select.bact),
      tools.andSelect(urinaryRemoved.v2.std, multi.select.bact),
      tools.andSelect(urinaryEnd.v2.std, multi.select.bact),
      tools.andSelect(urinaryDiag.v2, multi.select.bact),
      tools.andSelect(enfCatheter.v2, multi.select.bact),
      tools.andSelect(enfCatheterType.v3.all, multi.select.bact),
      tools.andSelect(enfInsertionDate.v2, multi.select.bact),
      tools.andSelect(enfPreviousManip, multi.select.bact),
      tools.andSelect(enfPreviousManipType.v1, multi.select.bact),
      tools.andSelect(enfPreviousManipDate, multi.select.bact),
      tools.andSelect(micro.v3.noVirus, multi.select.bact)
    ],

    extra: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v1, select.bact),
      tools.andSelect(bactOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v2.peri, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v1, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v3.woOmbilical, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v2.peri, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v1, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v3.noVirus, select.bact)
    ],

    neo: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v1, select.bact),
      tools.andSelect(neoOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v4.neo, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v1, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v3.woOmbilical, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(lipids.v3, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v4.neo, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v1, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v3.noVirus, select.bact)
    ],

    dia: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v1, select.bact),
      tools.andSelect(bactOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v2.dia, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v1, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v3.all, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(lipids.v3, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v2.dia, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v1, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v3.noVirus, select.bact)
    ]
  },

  v5: {
    flat: [
      episod,
      diagDate.v1,
      bactOrigin.v2,
      multi.covidStatus.std,
      entrance.v3.all,
      insertionDate.v2,
      previousManip,
      previousManipType.v1,
      previousManipDate,
      insertionSite.v3.all,
      removed.v2,
      removalDate.v2,
      tools.select(divDiag.v2, select.isKtEntrance.v2),
      clinicalSigns,
      bioSigns,
      adaptedAntibio,
      twoHemocs,
      lipids.v3,
      tools.select(intub.intubated.bact, select.pleu.v2),
      tools.andSelect(intub.intubStart.bact, select.pleu.v2),
      tools.andSelect(intub.reintubated.bact, select.pleu.v2),
      tools.andSelect(intub.intubEnd.bact, select.pleu.v2),
      urinary.v2.std,
      urinaryStart.v2.std,
      urinaryRemoved.v2.std,
      urinaryEnd.v2.std,
      urinaryDiag.v2,
      enfCatheter.v2,
      enfCatheterType.v3.all,
      enfInsertionDate.v2,
      enfPreviousManip,
      enfPreviousManipType.v1,
      enfPreviousManipDate,
      micro.v4.noVirus
    ],

    exp: [
      tools.andSelect(episod, multi.select.bact),
      tools.andSelect(diagDate.v1, multi.select.bact),
      tools.andSelect(bactOrigin.v2, multi.select.bact),
      tools.andSelect(multi.covidStatus.std, multi.select.bact),
      tools.andSelect(entrance.v3.all, multi.select.bact),
      tools.andSelect(insertionDate.v2, multi.select.bact),
      tools.andSelect(previousManip, multi.select.bact),
      tools.andSelect(previousManipType.v1, multi.select.bact),
      tools.andSelect(previousManipDate, multi.select.bact),
      tools.andSelect(insertionSite.v3.all, multi.select.bact),
      tools.andSelect(removed.v2, multi.select.bact),
      tools.andSelect(removalDate.v2, multi.select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), multi.select.bact),
      tools.andSelect(clinicalSigns, multi.select.bact),
      tools.andSelect(bioSigns, multi.select.bact),
      tools.andSelect(adaptedAntibio, multi.select.bact),
      tools.andSelect(twoHemocs, multi.select.bact),
      tools.andSelect(lipids.v3, multi.select.bact),
      tools.andSelect(tools.select(intub.intubated.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.intubStart.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.reintubated.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.intubEnd.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(urinary.v2.std, multi.select.bact),
      tools.andSelect(urinaryStart.v2.std, multi.select.bact),
      tools.andSelect(urinaryRemoved.v2.std, multi.select.bact),
      tools.andSelect(urinaryEnd.v2.std, multi.select.bact),
      tools.andSelect(urinaryDiag.v2, multi.select.bact),
      tools.andSelect(enfCatheter.v2, multi.select.bact),
      tools.andSelect(enfCatheterType.v3.all, multi.select.bact),
      tools.andSelect(enfInsertionDate.v2, multi.select.bact),
      tools.andSelect(enfPreviousManip, multi.select.bact),
      tools.andSelect(enfPreviousManipType.v1, multi.select.bact),
      tools.andSelect(enfPreviousManipDate, multi.select.bact),
      tools.andSelect(micro.v4.noVirus, multi.select.bact)
    ],

    extra: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v1, select.bact),
      tools.andSelect(bactOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v2.peri, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v1, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v3.woOmbilical, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v2.peri, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v1, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v4.noVirus, select.bact)
    ],

    neo: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v1, select.bact),
      tools.andSelect(neoOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v4.neo, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v1, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v3.woOmbilical, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(lipids.v3, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v4.neo, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v1, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v4.noVirus, select.bact)
    ],

    dia: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v1, select.bact),
      tools.andSelect(bactOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v2.dia, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v1, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v3.all, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(lipids.v3, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v2.dia, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v1, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v4.noVirus, select.bact)
    ]
  },

  v6: {
    flat: [
      episod,
      diagDate.v2,
      bactOrigin.v2,
      multi.covidStatus.std,
      episodImmuno,
      entrance.v3.all,
      insertionDate.v2,
      previousManip,
      previousManipType.v2,
      previousManipDate,
      insertionSite.v3.all,
      removed.v2,
      removalDate.v2,
      tools.select(divDiag.v2, select.isKtEntrance.v2),
      clinicalSigns,
      bioSigns,
      adaptedAntibio,
      twoHemocs,
      lipids.v3,
      tools.select(intub.intubated.bact, select.pleu.v2),
      tools.andSelect(intub.intubStart.bact, select.pleu.v2),
      tools.andSelect(intub.reintubated.bact, select.pleu.v2),
      tools.andSelect(intub.intubEnd.bact, select.pleu.v2),
      urinary.v2.std,
      urinaryStart.v2.std,
      urinaryRemoved.v2.std,
      urinaryEnd.v2.std,
      urinaryDiag.v2,
      enfCatheter.v2,
      enfCatheterType.v3.all,
      enfInsertionDate.v2,
      enfPreviousManip,
      enfPreviousManipType.v2,
      enfPreviousManipDate,
      micro.v5.noVirus
    ],

    exp: [
      tools.andSelect(episod, multi.select.bact),
      tools.andSelect(diagDate.v2, multi.select.bact),
      tools.andSelect(bactOrigin.v2, multi.select.bact),
      tools.andSelect(multi.covidStatus.std, multi.select.bact),
      tools.andSelect(episodImmuno, multi.select.bact),
      tools.andSelect(entrance.v3.all, multi.select.bact),
      tools.andSelect(insertionDate.v2, multi.select.bact),
      tools.andSelect(previousManip, multi.select.bact),
      tools.andSelect(previousManipType.v2, multi.select.bact),
      tools.andSelect(previousManipDate, multi.select.bact),
      tools.andSelect(insertionSite.v3.all, multi.select.bact),
      tools.andSelect(removed.v2, multi.select.bact),
      tools.andSelect(removalDate.v2, multi.select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), multi.select.bact),
      tools.andSelect(clinicalSigns, multi.select.bact),
      tools.andSelect(bioSigns, multi.select.bact),
      tools.andSelect(adaptedAntibio, multi.select.bact),
      tools.andSelect(twoHemocs, multi.select.bact),
      tools.andSelect(lipids.v3, multi.select.bact),
      tools.andSelect(tools.select(intub.intubated.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.intubStart.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.reintubated.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.intubEnd.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(urinary.v2.std, multi.select.bact),
      tools.andSelect(urinaryStart.v2.std, multi.select.bact),
      tools.andSelect(urinaryRemoved.v2.std, multi.select.bact),
      tools.andSelect(urinaryEnd.v2.std, multi.select.bact),
      tools.andSelect(urinaryDiag.v2, multi.select.bact),
      tools.andSelect(enfCatheter.v2, multi.select.bact),
      tools.andSelect(enfCatheterType.v3.all, multi.select.bact),
      tools.andSelect(enfInsertionDate.v2, multi.select.bact),
      tools.andSelect(enfPreviousManip, multi.select.bact),
      tools.andSelect(enfPreviousManipType.v2, multi.select.bact),
      tools.andSelect(enfPreviousManipDate, multi.select.bact),
      tools.andSelect(micro.v5.noVirus, multi.select.bact)
    ],

    extra: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v2, select.bact),
      tools.andSelect(bactOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v2.peri, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v2, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v3.woOmbilical, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v2.peri, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v2, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v5.noVirus, select.bact)
    ],

    neo: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v2, select.bact),
      tools.andSelect(neoOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v4.neo, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v2, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v3.woOmbilical, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(lipids.v3, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v4.neo, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v2, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v5.noVirus, select.bact)
    ],

    dia: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v2, select.bact),
      tools.andSelect(bactOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v2.dia, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v2, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v3.all, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(lipids.v3, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v2.dia, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v2, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v5.noVirus, select.bact)
    ]
  },

  v7: {
    flat: [
      episod,
      diagDate.v2,
      bactOrigin.v2,
      multi.covidStatus.std,
      episodImmuno,
      entrance.v3.all,
      insertionDate.v2,
      previousManip,
      previousManipType.v2,
      previousManipDate,
      insertionSite.v3.all,
      removed.v2,
      removalDate.v2,
      tools.select(divDiag.v2, select.isKtEntrance.v2),
      clinicalSigns,
      bioSigns,
      adaptedAntibio,
      twoHemocs,
      lipids.v3,
      tunneled,
      tools.select(intub.intubated.bact, select.pleu.v2),
      tools.andSelect(intub.intubStart.bact, select.pleu.v2),
      tools.andSelect(intub.reintubated.bact, select.pleu.v2),
      tools.andSelect(intub.intubEnd.bact, select.pleu.v2),
      urinary.v2.std,
      urinaryStart.v2.std,
      urinaryRemoved.v2.std,
      urinaryEnd.v2.std,
      urinaryDiag.v2,
      enfCatheter.v2,
      enfCatheterType.v3.all,
      enfInsertionDate.v2,
      enfPreviousManip,
      enfPreviousManipType.v2,
      enfPreviousManipDate,
      enfTunneled,
      micro.v6.noVirus
    ],

    exp: [
      tools.andSelect(episod, multi.select.bact),
      tools.andSelect(diagDate.v2, multi.select.bact),
      tools.andSelect(bactOrigin.v2, multi.select.bact),
      tools.andSelect(multi.covidStatus.std, multi.select.bact),
      tools.andSelect(episodImmuno, multi.select.bact),
      tools.andSelect(entrance.v3.all, multi.select.bact),
      tools.andSelect(insertionDate.v2, multi.select.bact),
      tools.andSelect(previousManip, multi.select.bact),
      tools.andSelect(previousManipType.v2, multi.select.bact),
      tools.andSelect(previousManipDate, multi.select.bact),
      tools.andSelect(insertionSite.v3.all, multi.select.bact),
      tools.andSelect(removed.v2, multi.select.bact),
      tools.andSelect(removalDate.v2, multi.select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), multi.select.bact),
      tools.andSelect(clinicalSigns, multi.select.bact),
      tools.andSelect(bioSigns, multi.select.bact),
      tools.andSelect(adaptedAntibio, multi.select.bact),
      tools.andSelect(twoHemocs, multi.select.bact),
      tools.andSelect(lipids.v3, multi.select.bact),
      tools.andSelect(tunneled, multi.select.bact),
      tools.andSelect(tools.select(intub.intubated.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.intubStart.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.reintubated.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.intubEnd.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(urinary.v2.std, multi.select.bact),
      tools.andSelect(urinaryStart.v2.std, multi.select.bact),
      tools.andSelect(urinaryRemoved.v2.std, multi.select.bact),
      tools.andSelect(urinaryEnd.v2.std, multi.select.bact),
      tools.andSelect(urinaryDiag.v2, multi.select.bact),
      tools.andSelect(enfCatheter.v2, multi.select.bact),
      tools.andSelect(enfCatheterType.v3.all, multi.select.bact),
      tools.andSelect(enfInsertionDate.v2, multi.select.bact),
      tools.andSelect(enfPreviousManip, multi.select.bact),
      tools.andSelect(enfPreviousManipType.v2, multi.select.bact),
      tools.andSelect(enfPreviousManipDate, multi.select.bact),
      tools.andSelect(enfTunneled, multi.select.bact),
      tools.andSelect(micro.v6.noVirus, multi.select.bact)
    ],

    extra: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v2, select.bact),
      tools.andSelect(bactOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v3.peri, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v2, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v3.woOmbilical, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v2.peri, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v2, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v6.noVirus, select.bact)
    ],

    neo: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v2, select.bact),
      tools.andSelect(neoOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v4.neo, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v2, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v3.woOmbilical, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(lipids.v3, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v4.neo, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v2, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v6.noVirus, select.bact)
    ],

    dia: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v2, select.bact),
      tools.andSelect(bactOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v2.dia, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v2, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v3.all, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(lipids.v3, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v2.dia, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v2, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v6.noVirus, select.bact)
    ]
  },

  v8: {
    flat: [
      episod,
      diagDate.v2,
      bactOrigin.v2,
      multi.covidStatus.std,
      episodImmuno,
      entrance.v3.all,
      insertionDate.v2,
      previousManip,
      previousManipType.v2,
      previousManipDate,
      insertionSite.v3.all,
      removed.v2,
      removalDate.v2,
      tools.select(divDiag.v2, select.isKtEntrance.v2),
      clinicalSigns,
      bioSigns,
      adaptedAntibio,
      twoHemocs,
      lipids.v4.std,
      tunneled,
      tools.select(intub.intubated.bact, select.pleu.v2),
      tools.andSelect(intub.intubStart.bact, select.pleu.v2),
      tools.andSelect(intub.reintubated.bact, select.pleu.v2),
      tools.andSelect(intub.intubEnd.bact, select.pleu.v2),
      urinary.v2.std,
      urinaryStart.v2.std,
      urinaryRemoved.v2.std,
      urinaryEnd.v2.std,
      urinaryDiag.v2,
      enfCatheter.v2,
      enfCatheterType.v3.all,
      enfInsertionDate.v2,
      enfPreviousManip,
      enfPreviousManipType.v2,
      enfPreviousManipDate,
      enfTunneled,
      micro.v6.noVirus
    ],

    exp: [
      tools.andSelect(episod, multi.select.bact),
      tools.andSelect(diagDate.v2, multi.select.bact),
      tools.andSelect(bactOrigin.v2, multi.select.bact),
      tools.andSelect(multi.covidStatus.std, multi.select.bact),
      tools.andSelect(episodImmuno, multi.select.bact),
      tools.andSelect(entrance.v3.all, multi.select.bact),
      tools.andSelect(insertionDate.v2, multi.select.bact),
      tools.andSelect(previousManip, multi.select.bact),
      tools.andSelect(previousManipType.v2, multi.select.bact),
      tools.andSelect(previousManipDate, multi.select.bact),
      tools.andSelect(insertionSite.v3.all, multi.select.bact),
      tools.andSelect(removed.v2, multi.select.bact),
      tools.andSelect(removalDate.v2, multi.select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), multi.select.bact),
      tools.andSelect(clinicalSigns, multi.select.bact),
      tools.andSelect(bioSigns, multi.select.bact),
      tools.andSelect(adaptedAntibio, multi.select.bact),
      tools.andSelect(twoHemocs, multi.select.bact),
      tools.andSelect(lipids.v4.std, multi.select.bact),
      tools.andSelect(tunneled, multi.select.bact),
      tools.andSelect(tools.select(intub.intubated.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.intubStart.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.reintubated.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(tools.andSelect(intub.intubEnd.bact, select.pleu.v2), multi.select.bact),
      tools.andSelect(urinary.v2.std, multi.select.bact),
      tools.andSelect(urinaryStart.v2.std, multi.select.bact),
      tools.andSelect(urinaryRemoved.v2.std, multi.select.bact),
      tools.andSelect(urinaryEnd.v2.std, multi.select.bact),
      tools.andSelect(urinaryDiag.v2, multi.select.bact),
      tools.andSelect(enfCatheter.v2, multi.select.bact),
      tools.andSelect(enfCatheterType.v3.all, multi.select.bact),
      tools.andSelect(enfInsertionDate.v2, multi.select.bact),
      tools.andSelect(enfPreviousManip, multi.select.bact),
      tools.andSelect(enfPreviousManipType.v2, multi.select.bact),
      tools.andSelect(enfPreviousManipDate, multi.select.bact),
      tools.andSelect(enfTunneled, multi.select.bact),
      tools.andSelect(micro.v6.noVirus, multi.select.bact)
    ],

    extra: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v2, select.bact),
      tools.andSelect(bactOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v3.peri, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v2, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v3.woOmbilical, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(lipids.v4.rea, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v2.peri, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v2, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v6.noVirus, select.bact)
    ],

    neo: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v2, select.bact),
      tools.andSelect(neoOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v5.neo, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v2, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v3.woOmbilical, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(lipids.v4.neo, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v4.neo, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v2, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v6.noVirus, select.bact)
    ],

    dia: [
      bacteremiaPresent,
      tools.andSelect(episod, select.bact),
      tools.andSelect(diagDate.v2, select.bact),
      tools.andSelect(bactOrigin.v2, select.bact),
      tools.andSelect(multi.covidStatus.std, select.bact),
      tools.andSelect(entrance.v2.dia, select.bact),
      tools.andSelect(insertionDate.v2, select.bact),
      tools.andSelect(previousManip, select.bact),
      tools.andSelect(previousManipType.v2, select.bact),
      tools.andSelect(previousManipDate, select.bact),
      tools.andSelect(insertionSite.v3.all, select.bact),
      tools.andSelect(removed.v2, select.bact),
      tools.andSelect(removalDate.v2, select.bact),
      tools.andSelect(tools.select(divDiag.v2, select.isKtEntrance.v2), select.bact),
      tools.andSelect(clinicalSigns, select.bact),
      tools.andSelect(bioSigns, select.bact),
      tools.andSelect(adaptedAntibio, select.bact),
      tools.andSelect(twoHemocs, select.bact),
      tools.andSelect(lipids.v4.std, select.bact),
      tools.andSelect(enfCatheter.v2, select.bact),
      tools.andSelect(enfCatheterType.v2.dia, select.bact),
      tools.andSelect(enfInsertionDate.v2, select.bact),
      tools.andSelect(enfPreviousManip, select.bact),
      tools.andSelect(enfPreviousManipType.v2, select.bact),
      tools.andSelect(enfPreviousManipDate, select.bact),
      tools.andSelect(micro.v6.noVirus, select.bact)
    ]
  }
}

const sepFields = {
  v1: {
    episod: episod,
    diagDate: diagDate.v1,
    origin: tools.cfg.list(fields.origin, 'Origine', go.fields.bactOriginNoEhpad),
    covidStatus: multi.covidStatus.std,
    entrance: entrance.v2.all,
    insertionDate: insertionDate.sep,
    insertionSite: insertionSite.sep.v1,
    removed: removed.sep,
    removalDate: removalDate.sep,
    lipids: lipids.sep.v1,
    intubated: tools.select(intub.intubated.std, select.pleu.sep),
    intubStart: tools.andSelect(intub.intubStart.sep, select.pleu.sep),
    urinary: urinary.v1.sep,
    urinaryStart: urinaryStart.v1.sep,
    urinaryRemoved: urinaryRemoved.v1.sep,
    urinaryEnd: urinaryEnd.v1.sep,
    enfCatheter: enfCatheter.sep,
    enfCatheterType: enfCatheterType.v2.sep,
    enfInsertionDate: enfInsertionDate.sep
  },

  v2: {
    episod: episod,
    diagDate: diagDate.v1,
    origin: tools.cfg.list(fields.origin, 'Origine', go.fields.bactOriginNoEhpad),
    covidStatus: multi.covidStatus.std,
    entrance: entrance.v3.all,
    insertionDate: insertionDate.sep,
    insertionSite: insertionSite.sep.v1,
    removed: removed.sep,
    removalDate: removalDate.sep,
    lipids: lipids.sep.v1,
    intubated: tools.select(intub.intubated.std, select.pleu.sep),
    intubStart: tools.andSelect(intub.intubStart.sep, select.pleu.sep),
    reintubated: tools.andSelect(intub.reintubated.sep, select.pleu.sep),
    intubEnd: tools.andSelect(intub.intubEnd.sep, select.pleu.sep),
    urinary: tools.select(urinary.v2.sep, select.isUriEntrance.sep),
    urinaryStart: urinaryStart.v1.sep,
    urinaryRemoved: urinaryRemoved.v1.sep,
    urinaryEnd: urinaryEnd.v1.sep,
    enfCatheter: enfCatheter.sep,
    enfCatheterType: enfCatheterType.v3.sep,
    enfInsertionDate: enfInsertionDate.sep
  },

  v3: {
    episod: episod,
    diagDate: diagDate.v1,
    origin: tools.cfg.list(fields.origin, 'Origine', go.fields.bactOriginNoEhpad),
    covidStatus: multi.covidStatus.std,
    entrance: entrance.v3.all,
    insertionDate: insertionDate.sep,
    insertionSite: insertionSite.sep.v2,
    removed: removed.sep,
    removalDate: removalDate.sep,
    lipids: lipids.sep.v2,
    intubated: tools.select(intub.intubated.std, select.pleu.sep),
    intubStart: tools.andSelect(intub.intubStart.sep, select.pleu.sep),
    reintubated: tools.andSelect(intub.reintubated.sep, select.pleu.sep),
    intubEnd: tools.andSelect(intub.intubEnd.sep, select.pleu.sep),
    urinary: tools.select(urinary.v2.sep, select.isUriEntrance.sep),
    urinaryStart: urinaryStart.v1.sep,
    urinaryRemoved: urinaryRemoved.v1.sep,
    urinaryEnd: urinaryEnd.v1.sep,
    enfCatheter: enfCatheter.sep,
    enfCatheterType: enfCatheterType.v3.sep,
    enfInsertionDate: enfInsertionDate.sep
  },

  v4: {
    episod: episod,
    diagDate: diagDate.v2,
    origin: tools.cfg.list(fields.origin, 'Origine', go.fields.bactOriginNoEhpad),
    covidStatus: multi.covidStatus.std,
    entrance: entrance.v3.all,
    insertionDate: insertionDate.sep,
    insertionSite: insertionSite.sep.v2,
    removed: removed.sep,
    removalDate: removalDate.sep,
    lipids: lipids.sep.v2,
    intubated: tools.select(intub.intubated.std, select.pleu.sep),
    intubStart: tools.andSelect(intub.intubStart.sep, select.pleu.sep),
    reintubated: tools.andSelect(intub.reintubated.sep, select.pleu.sep),
    intubEnd: tools.andSelect(intub.intubEnd.sep, select.pleu.sep),
    urinary: tools.select(urinary.v2.sep, select.isUriEntrance.sep),
    urinaryStart: urinaryStart.v1.sep,
    urinaryRemoved: urinaryRemoved.v1.sep,
    urinaryEnd: urinaryEnd.v1.sep,
    enfCatheter: enfCatheter.sep,
    enfCatheterType: enfCatheterType.v3.sep,
    enfInsertionDate: enfInsertionDate.sep
  },

  v5: {
    episod: episod,
    diagDate: diagDate.v2,
    origin: tools.cfg.list(fields.origin, 'Origine', go.fields.bactOriginNoEhpad),
    covidStatus: multi.covidStatus.std,
    entrance: entrance.v3.all,
    insertionDate: insertionDate.sep,
    insertionSite: insertionSite.sep.v2,
    removed: removed.sep,
    removalDate: removalDate.sep,
    lipids: lipids.sep.v3,
    intubated: tools.select(intub.intubated.std, select.pleu.sep),
    intubStart: tools.andSelect(intub.intubStart.sep, select.pleu.sep),
    reintubated: tools.andSelect(intub.reintubated.sep, select.pleu.sep),
    intubEnd: tools.andSelect(intub.intubEnd.sep, select.pleu.sep),
    urinary: tools.select(urinary.v2.sep, select.isUriEntrance.sep),
    urinaryStart: urinaryStart.v1.sep,
    urinaryRemoved: urinaryRemoved.v1.sep,
    urinaryEnd: urinaryEnd.v1.sep,
    enfCatheter: enfCatheter.sep,
    enfCatheterType: enfCatheterType.v3.sep,
    enfInsertionDate: enfInsertionDate.sep
  }

}

for (let v in sepFields) {
  const sf = sepFields[v]
  for (let k in sf) {
    const target = sf[k]
    const n = tools.andSelect(target, multi.select.sep)
    if (!n.field.startsWith('sep')) {
      n.field = `sep${n.field}`
    }
    sf[k] = n
  }
}

const sep = (function () {
  function build (v) {
    const result = []
    const ver = `v${v}`
    for (let f in sepFields[ver]) {
      result.push(sepFields[ver][f])
    }

    return result
  }

  return {
    v1: build(1),
    v2: build(2),
    v3: build(3),
    v4: build(4),
    v5: build(5)
  }
})()

export default {
  all: bacteremia,
  sep: sep,
  sepFields: sepFields,

  episod: episod,
  diagDate: diagDate,
  divDiag: divDiag,
  lipids: lipids,
  insertionDate: insertionDate,
  enfCatheterType: enfCatheterType,
  enfCatheter: enfCatheter,
  enfInsertionDate: enfInsertionDate,
  removalDate: removalDate,
  diaOrigin: diaOrigin,
  bactOrigin: bactOrigin,
  urinary: urinary,
  urinaryDiag: urinaryDiag,
  urinaryRemoved: urinaryRemoved,
  urinaryStart: urinaryStart,
  urinaryEnd: urinaryEnd,
  intubStart: intub.intubStart.bact,
  intubEnd: intub.intubEnd.bact,
  entrance: entrance,
  insertionSite: insertionSite,
  previousManip: previousManip,
  previousManipType: previousManipType,
  previousManipDate: previousManipDate,
  enfPreviousManipDate: enfPreviousManipDate,
  clinicalSigns: clinicalSigns,
  bioSigns: bioSigns,
  adaptedAntibio: adaptedAntibio,
  twoHemocs: twoHemocs,
  episodImmuno: episodImmuno,
  tunneled: tunneled,
  enfTunneled: enfTunneled
}
